import React, { useState, useEffect } from 'react'
import { useWindowSize } from 'react-use'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { css } from '@emotion/core'
import 'dayjs/locale/ja'
import 'react-dates/initialize'

import {
  mainContainerHeaderStyle,
  headerBarLeftWrapperStyle,
  tableHeaderThStyle,
  tableBodyTdStyle,
  tableBodyTdStyleUnread,
} from '@/constants/layout'

import {
  guestNameUnreadEnStyle,
  guestNameUnreadJaStyle,
  guestNameStyle,
  guestNameKanaStyle,
  companionStyle,
  reservationRoomNumbersStyle,
  selfCheckinTimeStyle,
  reloadWrapperStyle,
} from '@/components/pages/accommodation-management/list-layout'
import { putAdminReservationApprovedStatus } from '@/apis/aipass'
import { Button, ButtonType } from '@/components/atoms/button'
import { ReloadButton } from '@/components/atoms/reload-button'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { DetailedReserchPanel } from '@/components/pages/accommodation-management/_stay/detailed-reserch-panel'
import { dividedRoomNumber, dividedTypeName } from '@/libs/divide-list'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { ApprovedStatusType } from '@/models/reservation-approved-status'
import { useSearchedConditions } from '@/hooks/use-search-condition'
import { convertLabelWithEntireSearch, EntireSearchContentType } from '@/models/entire-search'
import { SortableTableHeader, SortKeyType } from '@/components/molecules/sortable-table-header'
import { SortKey } from '../list'
import { SearchReservationList } from '@/models/reservation'

type ContainerProps = {
  reservationList: SearchReservationList[]
  page: number
  setPage: (v: number) => void
  setIsLoading: (isLoading: boolean) => void
  hasList: number
  listTotal: number
  limit: number
  pagingIsDisabled: boolean
  isStatusApproved
  setIsStatusApproved
  onClickReserch: () => void
  onClickClearReserch: () => void
  detailedReserch: EntireSearchContentType
  setDetailedReserch: any
  sortKey: SortKeyType
  onChangeSortKey: (sort: SortKeyType) => void
  listScrollRef?: React.RefObject<HTMLDivElement>
}

export const StayList: React.FC<ContainerProps> = ({
  reservationList,
  page,
  setPage,
  setIsLoading,
  hasList,
  listTotal,
  limit,
  pagingIsDisabled,
  isStatusApproved,
  setIsStatusApproved,
  onClickReserch,
  onClickClearReserch,
  detailedReserch,
  setDetailedReserch,
  ...props
}) => {
  const windows = useWindowSize()
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const [isDropDownReserchPanelOpen, setIsDropDownReserchPanelOpen] = useState<boolean>(false)
  const [checkedStatusApproved] = useState<boolean>(false)
  const [checkedReservationIds, setCheckedReservationIds] = useState<object>({})
  const [checkedAllFlag, setCheckedAllFlag] = useState<boolean>(false)
  const [checkedTotalCount, setCheckedTotalCount] = useState<number>(0)
  const { SearchedConditionValues } = useSearchedConditions(convertLabelWithEntireSearch('stay'))

  // Checkbox when status update (single) is selected
  const handleCheckboxChange = (e, index) => {
    // Empty if already checked
    let newValue = ''
    if (e.target.value !== checkedReservationIds[index]) {
      newValue = e.target.value
      const newIds = { ...checkedReservationIds, [index]: newValue }
      setCheckedReservationIds(newIds)
    }

    if (e.target.value === checkedReservationIds[index]) {
      delete checkedReservationIds[index]
      const newIds = { ...checkedReservationIds, [index]: newValue }
      setCheckedReservationIds(newIds)
    }
    // EX) When checking 0 and 2 (when checking 1 and unchecking) {0: '123', 1: '', 2: '234'}
  }

  // Checkbox when status update (whole) is selected
  const handleAllCheckboxChange = index => {
    if (!checkedAllFlag) {
      const allIds = reservationList.map(v => v.reservationId)
      setCheckedAllFlag(true)
      setCheckedReservationIds(allIds)
    } else {
      setCheckedAllFlag(false)
      setCheckedReservationIds([])
    }
  }

  // Push booking id and approvedStatus on status update
  const onSubmit = async () => {
    try {
      // 完了後の一覧の取り直しまでローダーで画面を覆い、更新中のタブのクリックや同じ予約の選び直しによる二重送信を防ぐ。
      // ローダーはブラウザの戻る・進むによるタブの切り替えは防げないため、別のタブの一覧が並ぶことは list.tsx の取得の番号で防いでいる
      setIsLoading(true)
      // Format the array, excluding if it is empty
      const reservationIds: string[] = []

      Object.keys(checkedReservationIds).forEach(function (key) {
        if (checkedReservationIds[key] !== '') {
          reservationIds.push(checkedReservationIds[key])
        }
      })
      await putAdminReservationApprovedStatus({ reservationIds: reservationIds, approvedStatus: ApprovedStatusType.Checkout })
    } catch (error) {
      errorHandler(error)
    } finally {
      onClickReserch()
    }
  }

  // Number of status change checks
  useEffect(() => {
    let count = 0
    Object.keys(checkedReservationIds).forEach(key => {
      if (checkedReservationIds[key] !== '') {
        count++
      }
    })
    setCheckedTotalCount(count)
    if (count === 0) {
      setCheckedAllFlag(false)
    }
    if (count > 0 && count === hasList) {
      setCheckedAllFlag(true)
    } else {
      setCheckedAllFlag(false)
    }
  }, [checkedReservationIds])

  return (
    <>
      <div css={[mainContainerHeaderStyle, { minWidth: 'auto' }]}>
        <div css={headerBarLeftWrapperStyle}>
          {!isStatusApproved && (
            <>
              <div>
                <Button
                  buttonType={4}
                  width={i18n.language === 'en' ? 118 : 90}
                  height={32}
                  fontSize={12}
                  onClick={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                >
                  {t('Detailed search')}
                </Button>
                {isDropDownReserchPanelOpen && (
                  <DetailedReserchPanel
                    onClickReserch={onClickReserch}
                    onClickClearReserch={onClickClearReserch}
                    setIsDropDownReserchPanelOpen={() => setIsDropDownReserchPanelOpen(!isDropDownReserchPanelOpen)}
                    detailedReserch={detailedReserch}
                    setDetailedReserch={setDetailedReserch}
                  />
                )}
              </div>
              <div css={reloadWrapperStyle}>
                <ReloadButton onClick={onClickReserch} />
              </div>

              <SearchedConditionValues />
            </>
          )}
          {isStatusApproved && (
            <>
              <div css={selectedNumberStyle}>
                {checkedTotalCount} {t('item selected')}
              </div>
              <Button
                buttonType={checkedTotalCount ? ButtonType.Secondary : ButtonType.BorderDisabled}
                isDisabled={!checkedTotalCount}
                width={166}
                height={32}
                fontSize={12}
                marginLeft={24}
                onClick={() => {
                  onSubmit()
                  setIsStatusApproved(false)
                  setCheckedReservationIds({})
                  setCheckedAllFlag(false)
                }}
              >
                {t('Change to checkout')}
              </Button>
              <Button
                buttonType={4}
                width={102}
                height={32}
                fontSize={12}
                marginLeft={8}
                onClick={() => {
                  setIsStatusApproved(false)
                  setCheckedReservationIds({})
                  setCheckedAllFlag(false)
                }}
              >
                {t('Cancel')}
              </Button>
            </>
          )}
        </div>
        <AccommodationPaging page={page} setPage={setPage} total={listTotal} limit={limit} isDisabled={pagingIsDisabled} />
      </div>
      <div ref={props.listScrollRef} css={{ overflow: 'auto', height: windows.height - 218 }}>
        <table css={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {isStatusApproved && (
                <th css={[tableHeaderThStyle, { paddingLeft: 32 }]}>
                  <input
                    id="checkboxAll"
                    alt="checkbox"
                    type="image"
                    src={require(`@/static/images/check-box${checkedAllFlag === true ? '' : '_off'}.svg`)}
                    onClick={index => handleAllCheckboxChange(index)}
                  />
                </th>
              )}
              <SortableTableHeader
                name={SortKey.ReserveGuestName}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Guest')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.ReservationId}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Reservation ID')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.RoomType} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Room type')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.RoomNumber}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Room number')}
              </SortableTableHeader>
              <SortableTableHeader
                name={SortKey.CheckinDate}
                onChange={props.onChangeSortKey}
                value={props.sortKey}
                css={tableHeaderThStyle}
              >
                {t('Date of stay')}
              </SortableTableHeader>
              <SortableTableHeader name={SortKey.UpdatedAt} onChange={props.onChangeSortKey} value={props.sortKey} css={tableHeaderThStyle}>
                {t('Update date')}
              </SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {reservationList?.map((v, index) => {
              return (
                <Link
                  key={index}
                  css={[tableBodyTdStyleUnread, { display: 'table-row', ':hover': { color: '#000' } }]}
                  to={`/stay/${v.checkinId}?reservationId=${v.reservationId}`}
                >
                  {isStatusApproved && (
                    <td css={[tableBodyTdStyle, { paddingLeft: 32 }]} onClick={e => e.stopPropagation()}>
                      <input
                        id="reservationId"
                        alt="checkboxList"
                        value={v.reservationId}
                        type="image"
                        src={require(
                          `@/static/images/check-box${
                            checkedReservationIds[index] === v.reservationId || checkedStatusApproved ? '' : '_off'
                          }.svg`,
                        )}
                        onClick={e => {
                          e.preventDefault()
                          handleCheckboxChange(e, index)
                        }}
                      />
                    </td>
                  )}
                  <td css={[tableBodyTdStyle, !isStatusApproved ? { paddingLeft: 32 } : {}]}>
                    <div css={() => (i18n.language === 'en' ? guestNameUnreadEnStyle : guestNameUnreadJaStyle)}>
                      <div css={guestNameKanaStyle}>{v.guestNameKana ? v.guestNameKana : '-'}</div>
                      <div css={guestNameStyle}>{v.guestName ? v.guestName : '-'}</div>
                    </div>
                  </td>
                  <td css={reservationIdTdStyle}>
                    <div css={companionStyle}>{v.reservationId ? v.reservationId : '-'}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 200 }]}>
                    <div css={reservationRoomNumbersStyle}>{dividedTypeName(v.rooms)}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 130 }]}>
                    <div css={[reservationRoomNumbersStyle, { width: '100%' }]}>{dividedRoomNumber(v.rooms)}</div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 160 }]}>
                    <div css={selfCheckinTimeStyle}>
                      {v.checkinDate === null ? (
                        '-'
                      ) : (
                        <>
                          {dayjs(v.checkinDate).format(t('YYYY-MM-DD'))}
                          <span style={{ paddingLeft: 5 }}>
                            {v.nights ?? 0}
                            {t('Night')}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td css={[tableBodyTdStyle, { maxWidth: 175 }]}>
                    {v.approvedStatusUpdatedAtUnixtime ? dayjs.unix(v.approvedStatusUpdatedAtUnixtime).format(t('MM-DD HH:mm')) : '-'}
                  </td>
                </Link>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

const selectedNumberStyle = css({
  fontSize: 14,
  fontWeight: 'bold',
  margin: 'auto',
})

const reservationIdTdStyle = css(tableBodyTdStyle, {
  maxWidth: 180,
  '@media(max-width: 1080px)': {
    maxWidth: 140,
  },
})
